import {
  Box,
  FormControl,
  Grid,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { CommonBoxWithNumber, TitleSubTitle } from "@platformx/utilities";
import { useEffect, useState } from "react";
import { useCustomStyle } from "./ContentStatus.style";

interface ContentStatusOption {
  value: string;
  label: string;
  description?: string;
}

interface ContentStatusProps {
  titleSubTitles: {
    sectionTitle: string;
    sectionSubTitle: string;
    contentStatusTitle: string;
    contentStatusSubTitle: string;
    contentStatusFieldName: string;
  };
  sectionNumber: string;
  state: any;
  setState: (updater: (prevState: any) => any) => void;
  unsavedChanges: React.MutableRefObject<boolean>;
  handleChange?: (event: any) => void;
  contentRef: React.MutableRefObject<any>;
  handleOnBlur?: (event: any) => void;
  disabled?: boolean;
  required?: boolean;
  options?: ContentStatusOption[];
}

const ContentStatus: React.FC<ContentStatusProps> = ({
  titleSubTitles,
  sectionNumber,
  state,
  setState,
  unsavedChanges,
  contentRef,
  disabled = false,
  required = false,
  options = [],
}) => {
  const {
    sectionTitle,
    sectionSubTitle,
    contentStatusTitle,
    contentStatusSubTitle,
    contentStatusFieldName,
  } = titleSubTitles;

  const classes = useCustomStyle();

  // Default content status options
  const defaultOptions: ContentStatusOption[] = [
    {
      value: "Free",
      label: "Free",
    },
    {
      value: "Behind Login",
      label: "Behind Login",
    },
    {
      value: "Behind Subscription",
      label: "Behind Subscription",
    },
  ];

  // Use provided options or fall back to defaults
  const contentStatusOptions = options.length > 0 ? options : defaultOptions;

  // Local state for content status selection
  const [selectedContentStatus, setSelectedContentStatus] = useState<string>("");

  // Check if this is VOD content type
  const isVod = state?.CommonFields?.category === "Vod";

  // Sync local state with main state
  useEffect(() => {
    const currentValue = isVod
      ? state?.CommonFields?.[contentStatusFieldName]
      : state?.[contentStatusFieldName];

    if (currentValue && currentValue !== selectedContentStatus) {
      setSelectedContentStatus(currentValue);
    }
  }, [state, contentStatusFieldName, isVod, selectedContentStatus]);

  // Handle dropdown change
  const handleContentStatusChange = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;

    setSelectedContentStatus(value);
    handleDropdownChange({
      target: {
        name: contentStatusFieldName,
        value: value,
      },
    });
  };

  // Handle state updates for both VOD and regular content
  const handleDropdownChange = (event: { target: { name: string; value: string } }) => {
    const { name, value } = event.target;

    // Update content ref
    contentRef.current = { ...contentRef.current, [name]: value };

    // Update state based on content type
    if (isVod) {
      setState((prevState) => ({
        ...prevState,
        CommonFields: {
          ...prevState.CommonFields,
          [name]: value,
        },
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }

    // Mark as having unsaved changes
    unsavedChanges.current = true;
  };

  // Get current value for display
  const getCurrentValue = (): string => {
    const currentValue = isVod
      ? state?.CommonFields?.[contentStatusFieldName]
      : state?.[contentStatusFieldName];

    return currentValue || selectedContentStatus || contentStatusOptions[0]?.value || "";
  };

  return (
    <Box id='ContentStatus' className={classes.mainStyleWrapper}>
      <CommonBoxWithNumber
        number={sectionNumber}
        title={sectionTitle}
        titleVarient='h5bold'
        subTitleVarient='p4regular'
        subTitle={sectionSubTitle}>
        <Grid container>
          <Grid item xs={12} sm={5} md={5} lg={5} className='leftField'>
            <TitleSubTitle
              title={contentStatusTitle}
              subTitle={contentStatusSubTitle}
              titleVariant='h6medium'
              subTitleVariant='p4regular'
            />
          </Grid>
          <Grid item xs={12} sm={7} md={7} lg={7} className='textField'>
            <FormControl fullWidth disabled={disabled} required={required}>
              <Select
                labelId='content-status-label'
                id='content-status-select'
                value={getCurrentValue()}
                onChange={handleContentStatusChange}
                input={<OutlinedInput />}
                displayEmpty
                placeholder='Select content status'
                sx={{
                  "& .MuiSelect-select": {
                    paddingY: "12px",
                  },
                }}>
                {contentStatusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box>
                      <Box component='span'>{option.label}</Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </CommonBoxWithNumber>
    </Box>
  );
};

export default ContentStatus;
